import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MaskitoModule } from '@maskito/angular'
import { amountMask, CUSTOM_FIELD_TYPE, CustomFieldModel } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'
import { Subject, takeUntil } from 'rxjs'

@Component({
  selector: 'mmx-custom-fields-form',
  templateUrl: './custom-fields-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule,
    FlexLayoutModule,
    MaskitoModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CustomFieldsFormComponent,
      multi: true,
    },
  ],
})

export class CustomFieldsFormComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input()
  public fields: CustomFieldModel[] = []

  public CUSTOM_FIELD_TYPE = CUSTOM_FIELD_TYPE
  public formGroup = this.fb.group({})
  public amountMask = amountMask()

  private onChange: (value: Record<string, any>) => void = () => {}
  private onTouched: () => void = () => {}
  private onDestroy$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
  ) { }

  public ngOnInit() {
    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((value) => this.onChange?.(value))

    for (const field of this.fields) {
      if (!field.key) {
        continue
      }

      this.formGroup.addControl(field.key, this.fb.control(null))
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public writeValue(value: Record<string, any> | null) {
    if (!value) {
      this.formGroup.reset()
      return
    }

    const getValue = (field: CustomFieldModel) => {
      if (!field.key) {
        return null
      }

      if (field.type === CUSTOM_FIELD_TYPE.DROPDOWN && field.settings?.dropdownMultiple === true) {
        const dropdownValue = value[field.key] ?? []
        return Array.isArray(dropdownValue) ? dropdownValue : [dropdownValue]
      }

      return value[field.key] ?? null
    }

    const newValue = this.fields.reduce((acc, field) => {
      if (!field.key) {
        return acc
      }

      return {
        ...acc,
        [field.key]: getValue(field),
      }
    }, {})

    this.formGroup.patchValue(newValue)
  }

  public registerOnChange(fn: (value: Record<string, any>) => void) {
    this.onChange = fn
  }

  public registerOnTouched(fn: () => void) {
    this.onTouched = fn
  }
}
